import React, { useState } from 'react'
import { Box, Button, ButtonGroup, Dialog, Typography } from '@mui/material'
import ExpandMoreRoundedIcon from '@mui/icons-material/ExpandMoreRounded'
import Loader from 'src/designSystem/Loader'
import { EnrollmentState } from 'src/graphql.generated'
import MeruDate from 'src/legacy/lib/MeruDate'
import PracticesPerWeek from 'src/legacy/components/PracticesPerWeek'
import RestartIntake from 'src/participant/ParticipantActions/RestartIntake'
import SetParticipantAcceptedForm from 'src/participant/ParticipantActions/SetParticipantAcceptedForm'
import SetParticipantDeclinedForm from 'src/participant/ParticipantActions/SetParticipantDeclinedForm'
import SetProgramEndForm from 'src/participant/ParticipantActions/SetProgramEndForm'
import {
  availableProgramEndMetrics,
  ProgramEndMetrics,
} from 'src/participant/ParticipantActions/SetProgramEndForm/programEndMetricSchemas'
import CurrentModuleGoal from 'src/participant/ParticipantInfo/CurrentModuleGoal'
import ProgramThemeInfo from 'src/participant/ParticipantInfo/ProgramThemeInfo'
import useLoadParticipantInfoData from 'src/participant/ParticipantInfo/useLoadParticipantInfoData'
import { useIsManager } from 'src/session/useSession'
import { advancedColors, onDark } from '../theme'
import { drawerCardSx } from './Section'

const AFTER_REGISTRATION = new Set([
  EnrollmentState.IntakeCallScheduled,
  EnrollmentState.IntakeLoggedIn,
  EnrollmentState.IntakeRegistered,
])

const programIsEnding = (lastWeekNbr: number, programStartDate?: string) => {
  const daysFromProgramEnd = MeruDate.now().daysFromProgramEnd(programStartDate, lastWeekNbr)

  return daysFromProgramEnd !== null && daysFromProgramEnd > -5
}

type DialogKind = 'ACCEPT_INTO_PROGRAM' | 'PARTICIPANT_DECLINE' | 'PROGRAM_END'

// Its own component so Standard's participant queries run only once the card is opened.
const TherapyBody = ({ uid }: { uid: string }) => {
  const [dialog, setDialog] = useState<DialogKind | null>(null)
  const info = useLoadParticipantInfoData(uid)
  const isManager = useIsManager()
  const close = () => setDialog(null)
  const programHasStarted = Boolean(info.programStartDate && new Date() > new Date(info.programStartDate))
  const programEndMetrics = (info.programEndMetrics ?? [])
    .map((metric) => availableProgramEndMetrics[metric as keyof typeof availableProgramEndMetrics])
    .filter((metric): metric is keyof ProgramEndMetrics => Boolean(metric))

  return (
    <Box sx={{ bgcolor: advancedColors.surface, color: advancedColors.ink, px: 2, py: 1.5 }}>
      {info.loading && <Loader size="small" />}
      {!info.loading && info.error && (
        <Typography variant="subtitle2" sx={{ color: advancedColors.inkMuted }}>
          Therapy program details are unavailable.
        </Typography>
      )}
      {!info.loading && !info.error && (
        <>
          {info.state && AFTER_REGISTRATION.has(info.state) && (
            <ButtonGroup fullWidth sx={{ mt: 2 }}>
              <Button color="primary" fullWidth onClick={() => setDialog('ACCEPT_INTO_PROGRAM')}>
                Accept
              </Button>
              <Button fullWidth onClick={() => setDialog('PARTICIPANT_DECLINE')}>
                Decline
              </Button>
            </ButtonGroup>
          )}
          {info.state === EnrollmentState.InProgram && programHasStarted && !isManager && (
            <ButtonGroup fullWidth sx={{ mt: 2 }}>
              <Button color="primary" fullWidth onClick={() => setDialog('PROGRAM_END')}>
                {programIsEnding(info.lastWeekNbr, info.programStartDate) ? 'Complete Program' : 'Terminate Program'}
              </Button>
            </ButtonGroup>
          )}
          {info.state === EnrollmentState.DidNotStart && <RestartIntake uid={uid} />}
          <Dialog fullWidth maxWidth={dialog === 'PROGRAM_END' ? 'sm' : 'xs'} onClose={close} open={Boolean(dialog)}>
            {dialog === 'ACCEPT_INTO_PROGRAM' && (
              <SetParticipantAcceptedForm
                acceptanceCriteria={info.acceptanceCriteria}
                isHrvProgram={info.isHrvProgram}
                programHasLabWork={info.programHasLabWork}
                close={close}
                uid={uid}
              />
            )}
            {dialog === 'PARTICIPANT_DECLINE' && <SetParticipantDeclinedForm close={close} uid={uid} />}
            {dialog === 'PROGRAM_END' && info.indicators.engagement && info.programStartDate && (
              <SetProgramEndForm
                close={close}
                cancel={close}
                uid={uid}
                engagement={info.indicators.engagement}
                lastWeekNbr={info.lastWeekNbr}
                programStartDate={info.programStartDate}
                programEndMetrics={programEndMetrics}
                terminateProgram={!programIsEnding(info.lastWeekNbr, info.programStartDate)}
                programLoaded={info.programLoaded}
              />
            )}
          </Dialog>
          {info.programStartDate && info.programID && (
            <Box sx={{ mt: 2 }}>
              <ProgramThemeInfo
                uid={uid}
                programID={info.programID}
                programStartDate={info.programStartDate}
                timezone={info.timezone}
              />
            </Box>
          )}
          {info.programStartDate && (
            <Box sx={{ mt: 2 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1, mb: 0.5 }}>
                <Typography variant="subtitle1" sx={{ color: advancedColors.inkMuted }}>
                  Practices by module
                </Typography>
                <CurrentModuleGoal uid={uid} />
              </Box>
              <PracticesPerWeek
                programStartDate={info.programStartDate}
                completedResponses={info.indicators.completedResponses}
                lastWeekNbr={info.lastWeekNbr}
              />
            </Box>
          )}
        </>
      )}
    </Box>
  )
}

const TherapySection = ({ uid }: { uid: string }) => {
  const [open, setOpen] = useState(false)

  return (
    <Box component="section" sx={drawerCardSx}>
      <Box
        component="button"
        type="button"
        aria-expanded={open}
        onClick={() => setOpen((value) => !value)}
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: 1,
          width: '100%',
          px: 2,
          minHeight: 40,
          border: 0,
          background: 'transparent',
          cursor: 'pointer',
          color: 'inherit',
          textAlign: 'left',
          ...(open ? { borderBottom: `1px solid ${onDark.line}` } : undefined),
        }}
      >
        <Typography
          component="h2"
          variant="caption"
          sx={{ color: onDark.primary, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.08em' }}
        >
          Therapy
        </Typography>
        <ExpandMoreRoundedIcon
          fontSize="small"
          sx={{ color: onDark.secondary, transform: open ? 'rotate(180deg)' : undefined }}
        />
      </Box>
      {open && <TherapyBody uid={uid} />}
    </Box>
  )
}

export default TherapySection
